<script>
   /****************************************************************/
   /* PlotScores                                                   */
   /* --------------------                                         */
   /* shows plot with PLS-DA scores                                */
   /****************************************************************/

   import { ColormapLegend, Segments } from 'svelte-plots-basic/2d';
   import { Vector } from 'mdatools/arrays';
   import { mrange } from 'mdatools/stat';

   import { AXIS_COLOR, MARKERS, MARGINS } from '@mdatools-apps/shared/plots/Defaults.js';
   import { plsres_get } from '@mdatools-apps/wasm/pls.js';
   import { getColors, getClasses, CLASS_COLORS } from './Controls.svelte.js';
   import { csvRow, downloadCSV } from '@mdatools-apps/shared/utils';

   import Axes from '@mdatools-apps/shared/plots/Axes.svelte';
   import PlotSeries from '@mdatools-apps/shared/plots/PlotSeries.svelte';
   import { plotSize, legendNames } from '@mdatools-apps/shared/plots/layout.js';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();


   // get the parameters needed for the plot
   const resobj = $derived(props && params && props.res && params.resID ? props.res[params.resID] : null);
   const classresobj = $derived(props && params && props.classres && params.resID ? props.classres[params.resID] : null);


   // get vector with score values for given component
   function getValues(r, a) {
      if (!r || !a || a < 1 || a > r.ncomp) return;
      return new Vector(plsres_get(r, 'xscores', a))
   }

   let xValues = $derived(getValues(resobj, params.comp1));
   let yValues = $derived(getValues(resobj, params.comp2));
   let colors = $derived(getColors(classresobj));
   let pch = $derived(MARKERS[params.resID]);
   let labels = $derived(resobj ? resobj.objlabels : null)

   // reactive expression to generate axis labels
   let xLabel = $derived("LV" + params.comp1);
   let yLabel = $derived("LV" + params.comp2);

   // reactive expression to compute x-axis limits
   let limX = $derived(mrange(xValues));

   // reactive expression to compute x-axis limits
   let ly = $derived(mrange(yValues));
   let dy = $derived(ly ? (ly[1] - ly[0]) * 0.1 : 0)
   let limY = $derived(ly ?[ly[0], ly[1] + dy] : [0, 1]);

   // reactive expression to generate plot title
   let title = 'X-scores';

   // the drawn size of the plot, for the class names cut to fit their legend cells
   let size = $state([0, 0]);
   const legendLabels = $derived(classresobj && classresobj.ind ?
      legendNames([classresobj.classNames[1], classresobj.classNames[0]], size, MARGINS, true) : null);

   // name of the image and CSV files
   const fileName = 'plot-scores';

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {
      const { sep, dec } = props.data[params.resID];
      const classes = getClasses(classresobj);
      let out = csvRow(['Set', 'Labels', 'Classes', xLabel, yLabel], sep, dec);
      for (let i = 0; i < labels.length; i++) {
         out += csvRow([params.resID, labels[i], classes[i], xValues.v[i], yValues.v[i]], sep, dec);
      }
      downloadCSV(out, fileName);
   }

</script>

{#if xValues && yValues }
<!-- 'display: contents' leaves the layout to Axes -->
<div style="display: contents" {@attach plotSize(v => size = v)}>
<Axes {title} {limX} {limY} {xLabel} {yLabel} {fileName} {downloadCSVCallback}>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />


   <!-- plot series 1 -->
   <PlotSeries
      xValues={xValues}
      yValues={yValues}
      labels={labels}
      marker={pch}
      plotType="p"
      showLabels={params.showLabels}
      colors={colors}
      {selected}
      {callbacks}
   />


   <!-- colormap legend-->
   {#if classresobj && classresobj.ind}
    <ColormapLegend colmap={[CLASS_COLORS[1], CLASS_COLORS[0]]} breaks={[0, 1, 2]} labels={legendLabels}/>
   {/if}

</Axes>
</div>
{/if}

